import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Shield, Search as SearchIcon, Home as HomeIcon, Camera, CheckCircle2 } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { http } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import JsonLd, { FAQS } from "@/components/JsonLd";
import SeoBlock from "@/components/SeoBlock";

export default function Home() {
  const { t } = useI18n();
  const [featured, setFeatured] = useState([]);
  const [openFaq, setOpenFaq] = useState(-1);
  useEffect(() => {
    http.get("/properties").then((r) => setFeatured((r.data || []).slice(0, 6))).catch(() => {});
  }, []);

  const orgLd = {
    "@context": "https://schema.org", "@type": "RealEstateAgent",
    "name": "Woon Verder", "url": "https://woonverder.nl",
    "logo": "https://customer-assets-wrfwihn1.emergentagent.net/job_78adfbdd-52b0-4e44-92bc-80f4766e6ce9/artifacts/ls9rp1yc_ChatGPT%20Image%20Sep%203%2C%202026%2C%2009_24_57%20AM.png",
    "description": "Internationaal real-estate matching platform. Nederlandse launch, internationale ambitie.",
    "areaServed": [{"@type": "Country", "name": "Netherlands"}],
    "sameAs": []
  };
  const faqLd = {
    "@context": "https://schema.org", "@type": "FAQPage",
    "mainEntity": FAQS.map(f => ({"@type": "Question", "name": f.q, "acceptedAnswer": {"@type": "Answer", "text": f.a}}))
  };
  const webSiteLd = {
    "@context": "https://schema.org", "@type": "WebSite",
    "name": "Woon Verder", "url": "https://woonverder.nl",
    "inLanguage": ["nl-NL", "en", "de", "es"],
    "potentialAction": {"@type": "SearchAction", "target": "https://woonverder.nl/woningen?city={search_term_string}", "query-input": "required name=search_term_string"}
  };
  return (
    <>
      <JsonLd data={orgLd} id="ld-org" />
      <JsonLd data={faqLd} id="ld-faq" />
      <JsonLd data={webSiteLd} id="ld-site" />
      <SeoBlock />
      {/* HERO */}
      <section className="wv-hero-grain relative overflow-hidden bg-gradient-to-b from-[#F8F9FA] to-white pt-14 md:pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 wv-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[color:var(--wv-navy)]/5 text-[color:var(--wv-navy)] text-xs font-bold uppercase tracking-widest">
              <Sparkles className="w-3.5 h-3.5" /> {t("hero.badge")}
            </div>
            <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[color:var(--wv-obsidian)] leading-[1.05]" data-testid="hero-title">
              {t("hero.title")}
            </h1>
            <p className="mt-5 text-lg text-slate-600 max-w-2xl">{t("hero.subtitle")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/woning-aanmelden" className="wv-btn-primary inline-flex items-center gap-2" data-testid="hero-cta-seller">
                {t("cta.submit_property")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/woning-zoeken" className="wv-btn-green inline-flex items-center gap-2" data-testid="hero-cta-buyer">
                {t("cta.search_property")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm text-slate-600">
              <span className="inline-flex items-center gap-2"><Shield className="w-4 h-4 text-[color:var(--wv-green)]" /> {t("hero.verified")}</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[color:var(--wv-green)]" /> {t("hero.never_auto")}</span>
            </div>
          </div>
          <div className="lg:col-span-5 relative wv-fade-up d2">
            <div className="grid grid-cols-6 grid-rows-6 gap-3 h-[420px] sm:h-[520px]">
              <img src="https://images.unsplash.com/photo-1754553080373-3a806029796a?crop=entropy&cs=srgb&fm=jpg&q=85"
                   alt="Amsterdam" className="col-span-4 row-span-4 object-cover w-full h-full rounded-3xl shadow-xl" />
              <img src="https://images.unsplash.com/photo-1760072513367-55182245e76c?crop=entropy&cs=srgb&fm=jpg&q=85"
                   alt="Interior" className="col-span-2 row-span-3 object-cover w-full h-full rounded-2xl shadow-md" />
              <img src="https://images.unsplash.com/photo-1701422052273-fd673aed2d1c?crop=entropy&cs=srgb&fm=jpg&q=85"
                   alt="Living" className="col-span-3 row-span-2 object-cover w-full h-full rounded-2xl shadow-md" />
              <div className="col-span-3 row-span-3 rounded-2xl bg-[color:var(--wv-navy)] text-white p-5 flex flex-col justify-between">
                <p className="text-xs font-bold uppercase tracking-widest text-white/60">{t("hero.match_label")}</p>
                <div>
                  <p className="font-display text-6xl font-extrabold text-[color:var(--wv-green)] font-mono-num">96%</p>
                  <p className="text-sm text-white/80 mt-1">{t("hero.match_example")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TAGLINES */}
      <section className="border-y border-slate-100 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10 grid md:grid-cols-2 gap-6">
          <div className="p-6 rounded-2xl bg-[color:var(--wv-sand)]">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500">{t("hero.for_sellers")}</p>
            <p className="mt-2 font-display text-2xl font-bold text-[color:var(--wv-obsidian)] leading-snug">"{t("hero.tag_seller")}"</p>
          </div>
          <div className="p-6 rounded-2xl bg-[color:var(--wv-sand)]">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500">{t("hero.for_buyers")}</p>
            <p className="mt-2 font-display text-2xl font-bold text-[color:var(--wv-obsidian)] leading-snug">"{t("hero.tag_buyer")}"</p>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 md:py-24">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[color:var(--wv-green-dark)]">{t("section.offer")}</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[color:var(--wv-obsidian)] mt-2">{t("section.featured")}</h2>
          </div>
          <Link to="/woningen" className="text-sm font-semibold text-[color:var(--wv-navy)] hover:underline" data-testid="home-all-properties">
            {t("section.all_properties")} →
          </Link>
        </div>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((p, i) => (
            <PropertyCard key={p.id} p={p} matchScore={[96, 91, 88, 84, 81, 78][i]} />
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[color:var(--wv-obsidian)] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <p className="text-xs font-bold uppercase tracking-widest text-[color:var(--wv-green)]">{t("section.process")}</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold mt-2">{t("section.how_it_works")}</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              { icon: HomeIcon, title: t("home.step1_title"), desc: t("home.step1_desc") },
              { icon: Shield, title: t("home.step2_title"), desc: t("home.step2_desc") },
              { icon: SearchIcon, title: t("home.step3_title"), desc: t("home.step3_desc") },
            ].map((s, i) => (
              <div key={i} className="p-7 rounded-2xl bg-white/5 border border-white/10">
                <div className="w-12 h-12 rounded-xl bg-[color:var(--wv-green)] grid place-items-center">
                  <s.icon className="w-6 h-6 text-white" />
                </div>
                <p className="mt-4 text-xs font-bold text-white/50 uppercase tracking-widest">{t("home.step_label")} {i + 1}</p>
                <h3 className="font-display text-xl font-bold mt-1">{s.title}</h3>
                <p className="mt-2 text-sm text-white/70 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/hoe-het-werkt" className="wv-btn-green inline-flex items-center gap-2" data-testid="how-more-link">
              {t("home.full_explanation")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-16 md:py-24">
        <p className="text-xs font-bold uppercase tracking-widest text-[color:var(--wv-green-dark)]">{t("home.faq_kicker")}</p>
        <h2 className="font-display text-3xl md:text-4xl font-bold text-[color:var(--wv-obsidian)] mt-2">{t("home.faq_title")}</h2>
        <div className="mt-8 divide-y divide-slate-200 border border-slate-200 rounded-2xl bg-white">
          {FAQS.map((f, i) => (
            <div key={i}>
              <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full text-left p-5 flex items-start justify-between gap-4 hover:bg-slate-50" data-testid={`faq-toggle-${i}`}>
                <span className="font-display font-bold text-slate-900">{f.q}</span>
                <span className="font-mono-num text-slate-400 shrink-0">{openFaq === i ? "−" : "+"}</span>
              </button>
              {openFaq === i && <div className="px-5 pb-5 text-slate-700 leading-relaxed" data-testid={`faq-answer-${i}`}>{f.a}</div>}
            </div>
          ))}
        </div>
      </section>

      {/* SELLER BAND */}
      <section className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[color:var(--wv-green-dark)]">{t("home.seller_kicker")}</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[color:var(--wv-obsidian)] mt-2">
              {t("home.seller_band_title")}
            </h2>
            <p className="mt-4 text-slate-600">
              {t("home.seller_band_desc")}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/woning-aanmelden" className="wv-btn-primary inline-flex items-center gap-2" data-testid="seller-band-cta">
                <Camera className="w-4 h-4" /> {t("home.seller_band_cta")}
              </Link>
              <Link to="/fotografie" className="wv-btn-outline inline-flex items-center gap-2" data-testid="seller-band-photo-link">{t("home.seller_band_photo_cta")}</Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <img src="https://images.pexels.com/photos/6197232/pexels-photo-6197232.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Interior" className="rounded-2xl aspect-[3/4] object-cover" />
            <img src="https://images.pexels.com/photos/6908502/pexels-photo-6908502.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Interior 2" className="rounded-2xl aspect-[3/4] object-cover mt-8" />
          </div>
        </div>
      </section>
    </>
  );
}
